/**
 * AnalyticsPage — Auswertungsbereich (Jun-2026, User-Wunsch).
 *
 * Zwei Bereiche:
 *  1. Fahrzeugverkäufe — nach Zustand, Hersteller/Modell, Verkäufer; Ertrag/Marge.
 *  2. Werkstatt — Umsatz, Wareneinsatz, Monteurleistungen (Lohn/Stunden je Monteur).
 *
 * Datenquellen (Backend analytics_router):
 *  GET /api/dealer/analytics/vehicle-sales?date_from=&date_to=
 *  GET /api/dealer/analytics/workshop?date_from=&date_to=
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    BarChart3, Bike, Wrench, TrendingUp, Coins, Package, Users, Calendar, RefreshCw,
    Download, FileText, ArrowUp, ArrowDown, Trophy, Wallet, Settings, Loader2,
    Target, ClipboardList, CalendarClock, ChevronRight, XCircle,
} from "lucide-react";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
    PieChart, Pie, Legend,
} from "recharts";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import OrgBranchSwitcher from "../components/org/OrgBranchSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

const eur = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const num = (v) => Number(v || 0).toLocaleString("de-DE");
const PALETTE = ["#facc15", "#3b82f6", "#10b981", "#f97316", "#a855f7", "#ef4444", "#14b8a6", "#eab308"];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function yearStartISO() { return `${new Date().getFullYear()}-01-01`; }
function monthStartISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; }

/** Δ%-Badge gegenüber Vorjahr */
function DeltaBadge({ value }) {
    if (value === null || value === undefined) {
        return <span className="text-[11px] text-gray-400" title="Kein Vorjahreswert">—</span>;
    }
    const up = value > 0, flat = value === 0;
    const cls = flat ? "text-gray-500 bg-gray-100" : up ? "text-green-700 bg-green-100" : "text-red-700 bg-red-100";
    const Icon = flat ? null : up ? ArrowUp : ArrowDown;
    return (
        <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold ${cls}`}>
            {Icon && <Icon className="h-3 w-3" />}{value > 0 ? "+" : ""}{value}% <span className="font-normal text-gray-400">VJ</span>
        </span>
    );
}

/** KPI-Kachel */
function Kpi({ icon: Icon, label, value, sub, testid, accent = "text-yellow-400", delta }) {
    return (
        <div data-testid={testid} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                <Icon className={`h-4 w-4 ${accent}`} /> {label}
            </div>
            <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
            <div className="mt-0.5 flex items-center justify-between gap-2">
                {sub ? <span className="text-xs text-gray-500">{sub}</span> : <span />}
                {delta !== undefined && <DeltaBadge value={delta} />}
            </div>
        </div>
    );
}

function SectionCard({ title, icon: Icon, children, testid }) {
    return (
        <div data-testid={testid} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
                {Icon && <Icon className="h-4 w-4 text-gray-600" />} {title}
            </h3>
            {children}
        </div>
    );
}

function VehicleSales({ data }) {
    const navigate = useNavigate();
    const [drill, setDrill] = useState(null); // { title, rows }
    const openDrill = (title, predicate) => {
        const rows = (data?.vehicles || []).filter(predicate);
        setDrill({ title, rows });
    };
    const exportDrillCsv = (dr) => {
        const head = ["Fahrzeug", "Verkäufer", "Umsatz (netto)", "Ertrag", "Provision", "Datum", "ID"];
        const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const lines = [head.join(";")];
        for (const v of dr.rows) {
            lines.push([v.model_full, v.seller, v.provision ? "" : v.revenue_net,
                v.provision ? v.provision : v.margin, v.provision || "", v.date || "", v.id || ""].map(esc).join(";"));
        }
        const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${(dr.title || "fahrzeuge").replace(/[^a-z0-9]+/gi, "_")}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    };
    if (!data) return null;
    const s = data.summary || {};
    const d = data.delta || {};
    return (
        <div className="space-y-6" data-testid="analytics-vehicle-content">
            {drill && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="vs-drill-modal">
                    <div className="w-full max-w-3xl max-h-[80vh] overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between border-b px-5 py-3">
                            <h3 className="font-semibold text-gray-900">{drill.title} <span className="text-gray-400 font-normal">· {drill.rows.length} Fahrzeug(e)</span></h3>
                            <div className="flex items-center gap-3">
                                <button onClick={() => exportDrillCsv(drill)} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50" data-testid="vs-drill-export-csv">
                                    <Download className="h-3.5 w-3.5" /> CSV
                                </button>
                                <button onClick={() => setDrill(null)} className="text-gray-400 hover:text-gray-700 text-xl leading-none" data-testid="vs-drill-close">×</button>
                            </div>
                        </div>
                        <div className="overflow-y-auto p-2" style={{ maxHeight: "calc(80vh - 56px)" }}>
                            <table className="w-full text-sm">
                                <thead><tr className="text-left text-xs uppercase text-gray-400">
                                    <th className="py-1 px-2">Fahrzeug</th><th>Verkäufer</th>
                                    <th className="text-right">Umsatz</th><th className="text-right">Ertrag</th><th className="text-right">Datum</th><th></th>
                                </tr></thead>
                                <tbody>
                                    {drill.rows.map((v, i) => (
                                        <tr key={i} onClick={() => v.id && navigate(`/listing/${v.id}`)}
                                            className={`border-t border-gray-100 ${v.id ? "cursor-pointer hover:bg-yellow-100" : ""}`} data-testid={`vs-drill-row-${i}`}>
                                            <td className="py-1.5 px-2 font-medium text-gray-800">{v.model_full || "—"}</td>
                                            <td className="text-gray-600">{v.seller}</td>
                                            <td className="text-right">{v.provision ? <span className="text-gray-400" title="Vermittlung — Provision siehe Ertrag">—</span> : eur(v.revenue_net)}</td>
                                            <td className="text-right text-green-600">{v.provision ? eur(v.provision) : eur(v.margin)}</td>
                                            <td className="text-right text-gray-500">{v.date || "—"}</td>
                                            <td className="text-right pr-2"><ChevronRight className="inline h-4 w-4 text-gray-300" /></td>
                                        </tr>
                                    ))}
                                    {drill.rows.length === 0 && <tr><td colSpan={6} className="py-3 px-2 text-gray-500">Keine Fahrzeuge.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi icon={Bike} label="Verkaufte Fahrzeuge" value={num(s.count)} testid="vs-kpi-count" delta={d.count} />
                <Kpi icon={TrendingUp} label="Umsatz (netto)" value={eur(s.revenue_net)} sub={`Brutto ${eur(s.revenue_gross)}`} testid="vs-kpi-revenue" delta={d.revenue_net} />
                <Kpi icon={Coins} label="Einkauf (EK)" value={eur(s.cost)} testid="vs-kpi-cost" accent="text-orange-400" delta={d.cost} />
                <Kpi icon={TrendingUp} label="Ertrag / Marge" value={eur(s.margin)} sub="VK netto − EK" testid="vs-kpi-margin" accent="text-green-500" delta={d.margin} />
            </div>

            <SectionCard title="Verkaufte Fahrzeuge nach Art" icon={Bike} testid="vs-by-segment">
                <p className="mb-3 text-[11px] text-gray-500">Neuwagen · Gebrauchtwagen · Tageszulassungen · Vorführwagen · vermittelte Fahrzeuge · Fahrzeuge aus Leasingübernahmen (eindeutige Zuordnung je Fahrzeug).</p>
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase text-gray-400">
                        <th className="py-1">Fahrzeugart</th><th className="text-right">Stück</th><th className="text-right">Umsatz</th><th className="text-right">Ertrag</th>
                    </tr></thead>
                    <tbody>
                        {(data.by_segment || []).map((r, i) => {
                            const verm = r.segment === "Vermittelte Fahrzeuge";
                            return (
                                <tr key={i} onClick={() => openDrill(`Fahrzeugart: ${r.segment}`, (v) => v.segment === r.segment)}
                                    className="border-t border-gray-100 cursor-pointer hover:bg-yellow-50" data-testid={`vs-segment-row-${i}`}>
                                    <td className="py-1.5 font-medium text-gray-800">{r.segment}{verm && <span className="ml-1 text-[11px] font-normal text-gray-400">(erzielte Provision)</span>}</td>
                                    <td className="text-right">{num(r.count)}</td>
                                    <td className="text-right">{verm ? <span className="text-gray-400" title="Bei Vermittlung zählt nicht der Fahrzeugumsatz, sondern die erzielte Vermittlungsprovision (Spalte Ertrag/Provision).">—</span> : eur(r.revenue_net)}</td>
                                    <td className="text-right text-green-600" title={verm ? "Erzielte Vermittlungsprovision" : undefined}>{verm ? eur(r.provision) : eur(r.margin)}</td>
                                </tr>
                            );
                        })}
                        {(data.by_segment || []).length === 0 && <tr><td colSpan={4} className="py-3 text-gray-500">Keine Daten.</td></tr>}
                    </tbody>
                    {(data.by_segment || []).length > 0 && (
                        <tfoot><tr className="border-t-2 border-gray-300 font-semibold">
                            <td className="py-1.5">Gesamt</td>
                            <td className="text-right">{num(s.count)}</td>
                            <td className="text-right">{eur(s.revenue_net)}</td>
                            <td className="text-right text-green-700">{eur(s.margin)}</td>
                        </tr></tfoot>
                    )}
                </table>
                {data.purchases && (
                    <div className="mt-4 rounded-lg bg-orange-50 p-3 text-sm" data-testid="vs-purchases">
                        <span className="font-semibold text-orange-800">Eingekaufte Fahrzeuge im Zeitraum:</span>{" "}
                        {num(data.purchases.count)} Stück · {eur(data.purchases.cost)} (EK)
                        <span className="ml-1 text-[11px] text-gray-500">(Näherung über Anlagedatum)</span>
                    </div>
                )}
            </SectionCard>

            <div className="grid gap-5 lg:grid-cols-2">
                <SectionCard title="Verkäufe nach Zustand" icon={Bike} testid="vs-by-condition">
                    {(data.by_condition || []).length === 0 ? (
                        <p className="text-sm text-gray-500">Keine Daten im Zeitraum.</p>
                    ) : (
                        <>
                            <ResponsiveContainer width="100%" height={240}>
                                <PieChart>
                                    <Pie data={data.by_condition} dataKey="count" nameKey="condition" outerRadius={90} label>
                                        {data.by_condition.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v, n, p) => [`${v} Fzg.`, p?.payload?.condition]} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                            <table className="mt-3 w-full text-sm">
                                <thead><tr className="text-left text-xs uppercase text-gray-400">
                                    <th className="py-1">Zustand</th><th className="text-right">Anzahl</th><th className="text-right">Umsatz</th><th className="text-right">Marge</th>
                                </tr></thead>
                                <tbody>
                                    {data.by_condition.map((r, i) => (
                                        <tr key={i} onClick={() => openDrill(`Zustand: ${r.condition}`, (v) => v.condition === r.condition)}
                                            className="border-t border-gray-100 cursor-pointer hover:bg-yellow-50" data-testid={`vs-cond-row-${i}`}>
                                            <td className="py-1.5 font-medium text-gray-800">{r.condition}</td>
                                            <td className="text-right">{num(r.count)}</td>
                                            <td className="text-right">{eur(r.revenue_net)}</td>
                                            <td className="text-right text-green-600">{eur(r.margin)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </SectionCard>

                <SectionCard title="Top Hersteller" icon={BarChart3} testid="vs-by-brand">
                    {(data.by_brand || []).length === 0 ? (
                        <p className="text-sm text-gray-500">Keine Daten im Zeitraum.</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={data.by_brand} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis dataKey="brand" type="category" width={90} tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(v) => `${v} Fzg.`} />
                                <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </SectionCard>
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
                <SectionCard title="Auswertung nach Verkäufer" icon={Users} testid="vs-by-seller">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs uppercase text-gray-400">
                            <th className="py-1">Verkäufer</th><th className="text-right">Stück</th><th className="text-right">Umsatz</th><th className="text-right">Ertrag</th>
                        </tr></thead>
                        <tbody>
                            {(data.by_seller || []).map((r, i) => (
                                <tr key={i} onClick={() => openDrill(`Verkäufer: ${r.seller}`, (v) => v.seller === r.seller)}
                                    className="border-t border-gray-100 cursor-pointer hover:bg-yellow-50" data-testid={`vs-seller-row-${i}`}>
                                    <td className="py-1.5 font-medium text-gray-800">{r.seller}</td>
                                    <td className="text-right">{num(r.count)}</td>
                                    <td className="text-right">{eur(r.revenue_net)}</td>
                                    <td className="text-right text-green-600">{eur(r.margin)}</td>
                                </tr>
                            ))}
                            {(data.by_seller || []).length === 0 && <tr><td colSpan={4} className="py-3 text-gray-500">Keine Daten.</td></tr>}
                        </tbody>
                    </table>
                </SectionCard>

                <SectionCard title="Top Modelle" icon={Bike} testid="vs-by-model">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs uppercase text-gray-400">
                            <th className="py-1">Modell</th><th className="text-right">Stück</th><th className="text-right">Umsatz</th>
                        </tr></thead>
                        <tbody>
                            {(data.by_model || []).map((r, i) => (
                                <tr key={i} onClick={() => openDrill(`Modell: ${r.model || "—"}`, (v) => v.model_full === r.model)}
                                    className="border-t border-gray-100 cursor-pointer hover:bg-yellow-50" data-testid={`vs-model-row-${i}`}>
                                    <td className="py-1.5 font-medium text-gray-800">{r.model || "—"}</td>
                                    <td className="text-right">{num(r.count)}</td>
                                    <td className="text-right">{eur(r.revenue_net)}</td>
                                </tr>
                            ))}
                            {(data.by_model || []).length === 0 && <tr><td colSpan={3} className="py-3 text-gray-500">Keine Daten.</td></tr>}
                        </tbody>
                    </table>
                </SectionCard>
            </div>

            <SectionCard title="Umsatz / Ertrag je Monat" icon={Calendar} testid="vs-by-month">
                {(data.by_month || []).length === 0 ? (
                    <p className="text-sm text-gray-500">Keine Daten im Zeitraum.</p>
                ) : (
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={data.by_month}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => eur(v)} />
                            <Legend />
                            <Bar dataKey="revenue_net" name="Umsatz netto" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="margin" name="Ertrag" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </SectionCard>
        </div>
    );
}

function SellerRanking({ data }) {
    if (!data) return null;
    const s = data.summary || {};
    const d = data.delta || {};
    const ranking = data.ranking || [];
    const top = ranking.find((r) => r.seller_id) || ranking[0];
    return (
        <div className="space-y-6" data-testid="analytics-sellers-content">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi icon={Users} label="Aktive Verkäufer" value={num(s.sellers)} testid="sr-kpi-sellers" delta={d.sellers} />
                <Kpi icon={TrendingUp} label="Umsatz (netto)" value={eur(s.revenue_net)} sub={`Brutto ${eur(s.revenue_gross)}`} testid="sr-kpi-revenue" delta={d.revenue_net} />
                <Kpi icon={Coins} label="Ertrag / Rohertrag" value={eur(s.margin)} testid="sr-kpi-margin" accent="text-green-500" delta={d.margin} />
                <Kpi icon={Wallet} label="Provision (erwirtschaftet)" value={eur(s.provision_earned)} sub={`Bestätigt ${eur(s.provision_confirmed)}`} testid="sr-kpi-provision" accent="text-violet-400" delta={d.provision_earned} />
            </div>

            <SectionCard title="Verkäufer-Ranking (Provision aus Verkäufer-Provisionen)" icon={Trophy} testid="sr-ranking">
                <p className="text-[11px] text-gray-500 mb-3">Provisionssätze & Monatsziele werden zentral unter <b>Einstellungen → Provisionen</b> gepflegt. Bestätigung & Auszahlung im Buchhaltung-Tab „Provisionen".</p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]">
                        <thead><tr className="text-left text-xs uppercase text-gray-400">
                            <th className="py-1 w-10">#</th><th>Verkäufer</th>
                            <th className="text-right">Belege</th><th className="text-right">Umsatz</th><th className="text-right">Ertrag</th>
                            <th className="text-right">Prov.&nbsp;erwirt.</th><th className="text-right">Prov.&nbsp;best.</th>
                            <th className="text-right">Ziel/Mon</th><th className="text-right">Zielerreichung</th>
                        </tr></thead>
                        <tbody>
                            {ranking.map((r, i) => {
                                const ach = r.target_achievement_pct;
                                const achCls = ach == null ? "text-gray-400" : ach >= 100 ? "text-green-600 font-bold" : ach >= 70 ? "text-amber-600" : "text-red-500";
                                return (
                                    <tr key={i} className="border-t border-gray-100" data-testid={`sr-row-${i}`}>
                                        <td className="py-1.5 font-bold text-gray-500">{r.rank}</td>
                                        <td className="py-1.5 font-medium text-gray-800">{r.seller}</td>
                                        <td className="text-right">{num(r.invoices)}</td>
                                        <td className="text-right">{eur(r.revenue_net)}</td>
                                        <td className="text-right text-green-600">{eur(r.margin)}</td>
                                        <td className="text-right text-gray-500">{r.provision_earned ? eur(r.provision_earned) : "—"}</td>
                                        <td className="text-right font-medium text-violet-600">{r.provision_confirmed ? eur(r.provision_confirmed) : "—"}</td>
                                        <td className="text-right text-gray-500">{r.monthly_target_net ? eur(r.monthly_target_net) : "—"}</td>
                                        <td className={`text-right ${achCls}`}>{ach == null ? "—" : `${ach}%`}</td>
                                    </tr>
                                );
                            })}
                            {ranking.length === 0 && <tr><td colSpan={9} className="py-3 text-gray-500">Keine Daten im Zeitraum.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </SectionCard>

            <div className="grid gap-5 lg:grid-cols-2">
                <SectionCard title="Umsatz je Verkäufer" icon={BarChart3} testid="sr-chart">
                    {ranking.length === 0 ? (
                        <p className="text-sm text-gray-500">Keine Daten im Zeitraum.</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={ranking.slice(0, 12)} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis dataKey="seller" type="category" width={110} tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(v) => eur(v)} />
                                <Bar dataKey="revenue_net" name="Umsatz netto" fill="#facc15" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </SectionCard>

                <SectionCard title="Belege / Umsatz je Monat" icon={Calendar} testid="sr-by-month">
                    {(data.by_month || []).length === 0 ? (
                        <p className="text-sm text-gray-500">Keine Daten im Zeitraum.</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={data.by_month}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(v) => eur(v)} />
                                <Bar dataKey="revenue_net" name="Umsatz netto" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </SectionCard>
            </div>
        </div>
    );
}

function WorkshopReport({ data }) {
    if (!data) return null;
    const s = data.summary || {};
    const d = data.delta || {};
    // Aug-2026 (User-Wunsch): Kundendienst sieht keine Ertragszahlen
    const restricted = !!data.restricted;
    return (
        <div className="space-y-6" data-testid="analytics-workshop-content">
            <div className={`grid grid-cols-2 gap-3 ${restricted ? "" : "lg:grid-cols-4"}`}>
                <Kpi icon={Wrench} label="Aufträge" value={num(s.orders)} testid="ws-kpi-orders" delta={d.orders} />
                <Kpi icon={TrendingUp} label="Werkstattumsatz" value={eur(s.revenue_net)} sub={`Lohn ${eur(s.labour_revenue_net)} · Teile ${eur(s.parts_revenue_net)}`} testid="ws-kpi-revenue" delta={d.revenue_net} />
                {!restricted && <Kpi icon={Package} label="Wareneinsatz" value={eur(s.parts_cost)} sub="Teile-EK" testid="ws-kpi-parts-cost" accent="text-orange-400" delta={d.parts_cost} />}
                {!restricted && <Kpi icon={Coins} label="Rohertrag" value={eur(s.gross_profit)} sub={`${num(s.hours)} h · ${num(s.aw)} AW`} testid="ws-kpi-gross" accent="text-green-500" delta={d.gross_profit} />}
            </div>

            <SectionCard title="Werkstattumsatz – Aufteilung" icon={Coins} testid="ws-by-category">
                <p className="mb-2 text-[11px] text-gray-500">Lohn (Stunden &amp; €), Ersatzteile, Zubehör sowie Aufteilung nach Direktverkauf/Kunde (extern), Garantie (intern/extern), Gebrauchte (intern) und Auslieferung/Intern.</p>
                <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
                    <div className="rounded-lg bg-gray-50 p-3" data-testid="ws-split-labour">
                        <div className="text-xs uppercase text-gray-500">Lohn-Umsatz</div>
                        <div className="text-lg font-bold text-gray-900">{eur(s.labour_revenue_net)}</div>
                        <div className="text-xs text-gray-500">{num(s.hours)} Std. · {num(s.aw)} AW</div>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3" data-testid="ws-split-parts">
                        <div className="text-xs uppercase text-gray-500">Ersatzteile</div>
                        <div className="text-lg font-bold text-gray-900">{eur((s.parts_revenue_net || 0) - (s.accessory_revenue_net || 0))}</div>
                        <div className="text-xs text-gray-500">Teile gesamt {eur(s.parts_revenue_net)}</div>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3" data-testid="ws-split-accessory">
                        <div className="text-xs uppercase text-gray-500">Zubehör</div>
                        <div className="text-lg font-bold text-gray-900">{eur(s.accessory_revenue_net)}</div>
                        <div className="text-xs text-gray-500">Teil der Teile-Umsätze</div>
                    </div>
                </div>
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase text-gray-400">
                        <th className="py-1">Bereich</th><th className="text-right">Umsatz</th>
                        <th className="text-right">Lohn</th><th className="text-right">Teile</th>{!restricted && <th className="text-right">Rohertrag</th>}
                    </tr></thead>
                    <tbody>
                        {(data.by_category || []).map((r, i) => (
                            <tr key={i} className="border-t border-gray-100" data-testid={`ws-cat-row-${i}`}>
                                <td className="py-1.5 font-medium text-gray-800">{r.category}</td>
                                <td className="text-right">{eur(r.revenue_net)}</td>
                                <td className="text-right text-gray-600">{eur(r.labour_revenue_net)}</td>
                                <td className="text-right text-gray-600">{eur(r.parts_revenue_net)}</td>
                                {!restricted && <td className="text-right text-green-600">{eur(r.gross_profit)}</td>}
                            </tr>
                        ))}
                        {(data.by_category || []).length === 0 && <tr><td colSpan={restricted ? 4 : 5} className="py-3 text-gray-500">Keine Daten.</td></tr>}
                    </tbody>
                </table>
            </SectionCard>

            <SectionCard title="Monteurleistungen je Monteur" icon={Users} testid="ws-by-mechanic">
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase text-gray-400">
                        <th className="py-1">Monteur</th><th className="text-right">Aufträge</th>
                        <th className="text-right">Lohn-Umsatz</th><th className="text-right">Teile-Umsatz</th>
                        <th className="text-right">AW</th><th className="text-right">Std.</th>
                    </tr></thead>
                    <tbody>
                        {(data.by_mechanic || []).map((r, i) => (
                            <tr key={i} className="border-t border-gray-100" data-testid={`ws-mech-row-${i}`}>
                                <td className="py-1.5 font-medium text-gray-800">{r.mechanic}</td>
                                <td className="text-right">{num(r.orders)}</td>
                                <td className="text-right">{eur(r.labour_revenue_net)}</td>
                                <td className="text-right">{eur(r.parts_revenue_net)}</td>
                                <td className="text-right">{num(r.aw)}</td>
                                <td className="text-right">{num(r.hours)}</td>
                            </tr>
                        ))}
                        {(data.by_mechanic || []).length === 0 && <tr><td colSpan={6} className="py-3 text-gray-500">Keine Daten.</td></tr>}
                    </tbody>
                </table>
            </SectionCard>

            <SectionCard title="Werkstattumsatz je Monat" icon={Calendar} testid="ws-by-month">
                {(data.by_month || []).length === 0 ? (
                    <p className="text-sm text-gray-500">Keine Daten im Zeitraum.</p>
                ) : (
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={data.by_month}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => eur(v)} />
                            <Bar dataKey="revenue_net" name="Umsatz netto" fill="#facc15" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </SectionCard>

            {/* Jul-2026 (User-Wunsch) — Storno-Auswertung: Absage-Muster erkennen */}
            <SectionCard title="Storno-Auswertung (Absage-Muster)" icon={XCircle} testid="ws-cancellations">
                {(() => {
                    const c = data.cancellations || {};
                    if (!c.count) return <p className="text-sm text-gray-500">Keine stornierten Aufträge im Zeitraum. 👍</p>;
                    return (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                                <div className="rounded-lg bg-red-50 p-3" data-testid="ws-canc-count">
                                    <div className="text-xs uppercase text-red-700">Stornierte Aufträge</div>
                                    <div className="text-lg font-bold text-red-900">{num(c.count)}</div>
                                    <div className="text-xs text-red-700">{num(c.rate_percent)} % Storno-Quote</div>
                                </div>
                                <div className="rounded-lg bg-red-50 p-3" data-testid="ws-canc-lost">
                                    <div className="text-xs uppercase text-red-700">Entgangener Umsatz</div>
                                    <div className="text-lg font-bold text-red-900">{eur(c.lost_revenue_net)}</div>
                                    <div className="text-xs text-red-700">netto, lt. Auftragspositionen</div>
                                </div>
                                <div className="rounded-lg bg-gray-50 p-3" data-testid="ws-canc-top-reason">
                                    <div className="text-xs uppercase text-gray-500">Häufigster Grund</div>
                                    <div className="text-sm font-bold text-gray-900 truncate">{(c.by_reason || [])[0]?.reason || "—"}</div>
                                    <div className="text-xs text-gray-500">{num((c.by_reason || [])[0]?.count || 0)}× im Zeitraum</div>
                                </div>
                            </div>
                            <table className="w-full text-sm">
                                <thead><tr className="text-left text-xs uppercase text-gray-400">
                                    <th className="py-1">Storno-Grund</th><th className="text-right">Anzahl</th><th className="text-right">Entgangener Umsatz</th>
                                </tr></thead>
                                <tbody>
                                    {(c.by_reason || []).map((r, i) => (
                                        <tr key={i} className="border-t border-gray-100" data-testid={`ws-canc-reason-${i}`}>
                                            <td className="py-1.5 font-medium text-gray-800">„{r.reason}&quot;</td>
                                            <td className="text-right">{num(r.count)}</td>
                                            <td className="text-right text-red-600">{eur(r.lost_revenue_net)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {(c.recent || []).length > 0 && (
                                <div>
                                    <div className="text-xs uppercase text-gray-400 mb-1">Letzte Stornierungen</div>
                                    <div className="space-y-1">
                                        {c.recent.map((r, i) => (
                                            <div key={i} className="flex flex-wrap gap-x-3 text-xs text-gray-600 border-t border-gray-100 py-1" data-testid={`ws-canc-recent-${i}`}>
                                                <span className="font-mono-num font-bold">{r.order_number}</span>
                                                <span>{r.at}</span>
                                                {r.customer && <span>{r.customer}</span>}
                                                <span className="italic">„{r.reason}&quot;</span>
                                                <span className="ml-auto text-red-600">{eur(r.lost_revenue_net)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}
            </SectionCard>
        </div>
    );
}

function OperationsReport({ data }) {
    if (!data) return null;
    const p = data.personnel || {};
    const g = data.goods_purchase || {};
    const abs = data.absences || {};
    return (
        <div className="space-y-6" data-testid="analytics-operations-content">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                <Kpi icon={Wallet} label="Personalkosten (Richtwert)" value={eur(p.total)} sub={`${num(data.months)} Monat(e)`} testid="op-kpi-personnel" accent="text-violet-400" />
                <Kpi icon={Package} label="Wareneinkauf" value={eur(g.gross)} sub={`Netto ${eur(g.net)} · ${num(g.orders)} Bestellungen`} testid="op-kpi-goods" accent="text-orange-400" />
                <Kpi icon={Calendar} label="Abwesenheitstage" value={num((abs.by_kind || []).reduce((a, r) => a + (r.days || 0), 0))} sub="Urlaub · Krank · Schule · Ausgleich" testid="op-kpi-absences" />
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
                <SectionCard title="Personalkosten je Mitarbeiter" icon={Users} testid="op-personnel">
                    <p className="mb-2 text-[11px] text-gray-500">{p.note}</p>
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs uppercase text-gray-400">
                            <th className="py-1">Mitarbeiter</th><th className="text-right">Art</th>
                            <th className="text-right">€/Monat</th><th className="text-right">Zeitraum</th>
                        </tr></thead>
                        <tbody>
                            {(p.employees || []).map((r, i) => (
                                <tr key={i} className="border-t border-gray-100" data-testid={`op-personnel-row-${i}`}>
                                    <td className="py-1.5 font-medium text-gray-800">{r.name}</td>
                                    <td className="text-right text-gray-500">{r.salary_type === "fixed" ? "Festbezug" : "Stundenlohn"}</td>
                                    <td className="text-right">{eur(r.monthly_cost)}</td>
                                    <td className="text-right font-medium">{eur(r.period_cost)}</td>
                                </tr>
                            ))}
                            {(p.employees || []).length === 0 && <tr><td colSpan={4} className="py-3 text-gray-500">Keine Lohn-Stammdaten hinterlegt.</td></tr>}
                        </tbody>
                        {(p.employees || []).length > 0 && (
                            <tfoot><tr className="border-t-2 border-gray-300 font-semibold">
                                <td className="py-1.5" colSpan={3}>Gesamt</td>
                                <td className="text-right">{eur(p.total)}</td>
                            </tr></tfoot>
                        )}
                    </table>
                </SectionCard>

                <SectionCard title="In Anspruch genommene Tage" icon={Calendar} testid="op-absences">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs uppercase text-gray-400">
                            <th className="py-1">Art</th><th className="text-right">Tage</th>
                        </tr></thead>
                        <tbody>
                            {(abs.by_kind || []).map((r, i) => (
                                <tr key={i} className="border-t border-gray-100" data-testid={`op-absence-kind-${r.key}`}>
                                    <td className="py-1.5 font-medium text-gray-800">{r.label}</td>
                                    <td className="text-right">{num(r.days)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {(abs.by_employee || []).length > 0 && (
                        <div className="mt-4">
                            <div className="mb-1 text-xs uppercase text-gray-400">Je Mitarbeiter</div>
                            <table className="w-full text-sm">
                                <thead><tr className="text-left text-xs uppercase text-gray-400">
                                    <th className="py-1">Mitarbeiter</th><th className="text-right">Url.</th>
                                    <th className="text-right">Krank</th><th className="text-right">Schule</th>
                                    <th className="text-right">Ausgl.</th><th className="text-right">Σ</th>
                                </tr></thead>
                                <tbody>
                                    {abs.by_employee.map((r, i) => (
                                        <tr key={i} className="border-t border-gray-100" data-testid={`op-absence-emp-${i}`}>
                                            <td className="py-1.5 font-medium text-gray-800">{r.name}</td>
                                            <td className="text-right">{num(r.urlaub)}</td>
                                            <td className="text-right">{num(r.krank)}</td>
                                            <td className="text-right">{num(r.schule)}</td>
                                            <td className="text-right">{num(r.ausgleich)}</td>
                                            <td className="text-right font-semibold">{num(r.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </SectionCard>
            </div>

            <SectionCard title="Wareneinkauf" icon={Package} testid="op-goods">
                <div className="grid grid-cols-3 gap-3 text-center">
                    <div><div className="text-xs uppercase text-gray-500">Bestellungen</div><div className="text-xl font-bold">{num(g.orders)}</div></div>
                    <div><div className="text-xs uppercase text-gray-500">Netto</div><div className="text-xl font-bold">{eur(g.net)}</div></div>
                    <div><div className="text-xs uppercase text-gray-500">Brutto</div><div className="text-xl font-bold">{eur(g.gross)}</div></div>
                </div>
            </SectionCard>
        </div>
    );
}

const POT_ICON = {
    workshop_open: Wrench, workshop_quote: ClipboardList, workshop_completed: Wrench,
    vehicles_reserved: Bike, vehicles_sold: Bike, leasing_expiring: CalendarClock,
};

function PotentialReport({ data }) {
    const navigate = useNavigate();
    const [filter, setFilter] = useState("all");
    if (!data) return null;
    const s = data.summary || {};
    const cats = data.categories || [];
    const items = (data.items || []).filter((it) => filter === "all" || it.category === filter);
    const itemHref = (it) => {
        if (it.target === "workshop" && it.target_id) return `/dealer/workshop-orders?focus=${it.target_id}`;
        if (it.target === "leasing" && it.target_id) return `/dealer/leasingrueckgabe?open=${it.target_id}`;
        if (it.target === "listing" && it.target_id) return `/listing/${it.target_id}`;
        return null;
    };
    return (
        <div className="space-y-6" data-testid="analytics-potential-content">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                <Kpi icon={Target} label="Offene Positionen" value={num(s.positions)} testid="pot-kpi-positions" accent="text-violet-400" />
                <Kpi icon={Coins} label="Offenes Volumen (brutto)" value={eur(s.amount_gross)} sub="Noch nicht abgerechnet" testid="pot-kpi-amount" accent="text-green-500" />
                <Kpi icon={CalendarClock} label="Leasingausläufer (akt. Monat)" value={num((cats.find((c) => c.key === "leasing_expiring") || {}).count)} sub={`${data.current_month?.from || ""} – ${data.current_month?.to || ""}`} testid="pot-kpi-leasing" accent="text-orange-400" />
            </div>

            <SectionCard title="Potenziale nach Art" icon={Target} testid="pot-by-category">
                <p className="mb-3 text-[11px] text-gray-500">Live-Übersicht aller offenen, noch nicht abgerechneten Umsatzpositionen (unabhängig vom gewählten Zeitraum). Klick auf eine Kachel filtert die Liste unten.</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {cats.map((c) => {
                        const Icon = POT_ICON[c.key] || Coins;
                        const active = filter === c.key;
                        return (
                            <button key={c.key} onClick={() => setFilter(active ? "all" : c.key)}
                                className={`rounded-xl border p-4 text-left transition-colors ${active ? "border-yellow-400 bg-yellow-50" : "border-gray-200 bg-white hover:border-gray-300"}`}
                                data-testid={`pot-cat-${c.key}`}>
                                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                                    <Icon className="h-4 w-4 text-gray-600" /> {c.label}
                                </div>
                                <div className="mt-2 flex items-baseline justify-between">
                                    <span className="text-2xl font-bold text-gray-900">{num(c.count)}</span>
                                    <span className="text-sm font-semibold text-green-600">{eur(c.amount_gross)}</span>
                                </div>
                                <div className="mt-1 text-[11px] text-gray-400">{c.hint}</div>
                            </button>
                        );
                    })}
                </div>
            </SectionCard>

            <SectionCard title={`Offene Positionen${filter !== "all" ? " · gefiltert" : ""}`} icon={ClipboardList} testid="pot-items">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[640px]">
                        <thead><tr className="text-left text-xs uppercase text-gray-400">
                            <th className="py-1">Art</th><th>Referenz</th><th>Bezeichnung</th>
                            <th className="text-right">Betrag (brutto)</th><th className="text-right">Datum</th><th className="text-right">Status</th><th></th>
                        </tr></thead>
                        <tbody>
                            {items.map((it, i) => {
                                const href = itemHref(it);
                                const needsAmount = it.target === "workshop" && (it.amount_gross || 0) === 0;
                                return (
                                    <tr key={i}
                                        onClick={href ? () => navigate(href) : undefined}
                                        className={`border-t border-gray-100 ${needsAmount ? "bg-amber-50" : ""} ${href ? "cursor-pointer hover:bg-yellow-100" : ""}`}
                                        data-testid={`pot-item-row-${i}`}>
                                        <td className="py-1.5 text-gray-500">{it.category_label}</td>
                                        <td className="font-medium text-gray-800">{it.ref}</td>
                                        <td className="text-gray-700">{it.label}</td>
                                        <td className="text-right">
                                            {needsAmount ? (
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="text-amber-700 font-semibold">0,00 €</span>
                                                    <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800" data-testid={`pot-item-needs-amount-${i}`}>Beträge ergänzen</span>
                                                </span>
                                            ) : eur(it.amount_gross)}
                                        </td>
                                        <td className="text-right text-gray-500">{it.date || "—"}</td>
                                        <td className="text-right text-gray-500">{it.status}</td>
                                        <td className="text-right pl-2">{href && <ChevronRight className="inline h-4 w-4 text-gray-300" />}</td>
                                    </tr>
                                );
                            })}
                            {items.length === 0 && <tr><td colSpan={7} className="py-3 text-gray-500">Keine offenen Positionen.</td></tr>}
                        </tbody>
                    </table>
                </div>
                <p className="mt-2 text-[11px] text-gray-400">Zeile anklicken öffnet den jeweiligen Datensatz (Werkstattauftrag, Leasingvorgang bzw. Fahrzeug). Je Art werden max. 100 Positionen gelistet.</p>
            </SectionCard>
        </div>
    );
}

export default function AnalyticsPage() {
    const { user } = useAuth();
    // Aug-2026 (User-Wunsch): Kundendienst sieht NUR den Werkstatt-Reiter
    const restricted = user?.role === "zweiradverkaeufer" && user?.employee_function === "kundendienst";
    const initialTab = (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tab")) || "vehicles";
    const [from, setFrom] = useState(yearStartISO());
    const [to, setTo] = useState(todayISO());
    const [tab, setTab] = useState(["vehicles", "workshop", "sellers", "operations", "potential"].includes(initialTab) ? initialTab : "vehicles");
    // Mehr-Standort (Jun-2026): Hauptsitz kann den Auswertungs-Scope auf eine
    // Filiale umschalten. Default „__all__" = Aggregat über alle Standorte.
    const [orgBranch, setOrgBranch] = useState("__all__");
    const [vehicles, setVehicles] = useState(null);
    const [workshop, setWorkshop] = useState(null);
    const [sellers, setSellers] = useState(null);
    const [operations, setOperations] = useState(null);
    const [potential, setPotential] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { date_from: from, date_to: to, branch: orgBranch || "__all__" };
            if (restricted) {
                const ws = await api.get("/dealer/analytics/workshop", { params });
                setWorkshop(ws.data);
                return;
            }
            const [vs, ws, sr, op, pt] = await Promise.all([
                api.get("/dealer/analytics/vehicle-sales", { params }),
                api.get("/dealer/analytics/workshop", { params }),
                api.get("/dealer/analytics/seller-ranking", { params }),
                api.get("/dealer/analytics/operations", { params }),
                api.get("/dealer/analytics/potential", { params: { branch: orgBranch || "__all__" } }),
            ]);
            setVehicles(vs.data);
            setWorkshop(ws.data);
            setSellers(sr.data);
            setOperations(op.data);
            setPotential(pt.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Auswertung konnte nicht geladen werden");
        } finally {
            setLoading(false);
        }
    }, [from, to, orgBranch, restricted]);

    useEffect(() => { if (restricted) setTab("workshop"); }, [restricted]);

    const exportFile = async (fmt) => {
        const section = tab === "workshop" ? "workshop" : tab === "sellers" ? "sellers" : "vehicles";
        try {
            const res = await api.get(`/dealer/analytics/export.${fmt}`, {
                params: { section, date_from: from, date_to: to, branch: orgBranch || "__all__" },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `auswertung_${section}_${from}_${to}.${fmt}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Export fehlgeschlagen");
        }
    };

    useEffect(() => { load(); }, [load]);

    // Recharts ResponsiveContainer kann beim ersten Paint eine leere Fläche zeigen,
    // bis das Layout final ist — nach dem Laden ein Resize-Event auslösen erzwingt
    // ein Neuberechnen der Diagramm-Maße.
    useEffect(() => {
        if (vehicles || workshop || sellers || operations) {
            const t = setTimeout(() => window.dispatchEvent(new Event("resize")), 120);
            return () => clearTimeout(t);
        }
    }, [vehicles, workshop, sellers, operations, tab]);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Dark header (konsistent mit Rechnungen/Disposition) */}
            <div className="bg-[#111827] text-white">
                <div className="mx-auto max-w-7xl px-4 py-8">
                    <h1 className="mt-1 flex items-center gap-2 text-3xl font-bold" data-testid="analytics-title">
                        <BarChart3 className="h-7 w-7 text-yellow-400" /> Auswertungsbereich
                    </h1>
                    <p className="mt-1 text-sm text-gray-300">
                        {restricted ? "Werkstattumsätze und Monteurleistungen auf einen Blick." : "Verkaufszahlen, Erträge und Werkstattleistungen auf einen Blick."}
                    </p>

                    <div className="mt-5 flex flex-wrap items-end gap-3">
                        <div>
                            <label className="mb-1 block text-xs text-gray-400">Von</label>
                            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                                className="h-9 w-40 bg-white text-gray-900" data-testid="analytics-date-from" />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs text-gray-400">Bis</label>
                            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                                className="h-9 w-40 bg-white text-gray-900" data-testid="analytics-date-to" />
                        </div>
                        <Button onClick={load} disabled={loading}
                            className="h-9 bg-yellow-400 text-gray-900 hover:bg-yellow-300" data-testid="analytics-refresh">
                            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
                        </Button>
                        <Button onClick={() => { setFrom(monthStartISO()); setTo(todayISO()); }} variant="outline"
                            className="h-9 border-gray-500 bg-transparent text-white hover:bg-white/10" data-testid="analytics-preset-month">
                            <Calendar className="mr-1.5 h-4 w-4" /> Aktueller Monat
                        </Button>
                        <div className="ml-2">
                            <OrgBranchSwitcher user={user} value={orgBranch === "__all__" ? "__all__" : orgBranch}
                                onChange={(b) => setOrgBranch(b || "self")} dark allowAll />
                        </div>
                        <div className="ml-auto flex gap-2">
                            {!restricted && (
                                <>
                            <Button onClick={() => exportFile("csv")} variant="outline"
                                className="h-9 border-gray-500 bg-transparent text-white hover:bg-white/10" data-testid="analytics-export-csv">
                                <Download className="mr-1.5 h-4 w-4" /> CSV
                            </Button>
                            <Button onClick={() => exportFile("pdf")} variant="outline"
                                className="h-9 border-gray-500 bg-transparent text-white hover:bg-white/10" data-testid="analytics-export-pdf">
                                <FileText className="mr-1.5 h-4 w-4" /> PDF
                            </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-4 py-6">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="mb-5">
                        {!restricted && (
                        <TabsTrigger value="vehicles" data-testid="analytics-tab-vehicles">
                            <Bike className="mr-1.5 h-4 w-4" /> Fahrzeugverkäufe
                        </TabsTrigger>
                        )}
                        <TabsTrigger value="workshop" data-testid="analytics-tab-workshop">
                            <Wrench className="mr-1.5 h-4 w-4" /> Werkstatt
                        </TabsTrigger>
                        {!restricted && (
                        <>
                        <TabsTrigger value="sellers" data-testid="analytics-tab-sellers">
                            <Trophy className="mr-1.5 h-4 w-4" /> Verkäufer-Ranking
                        </TabsTrigger>
                        <TabsTrigger value="operations" data-testid="analytics-tab-operations">
                            <Wallet className="mr-1.5 h-4 w-4" /> Betrieb
                        </TabsTrigger>
                        <TabsTrigger value="potential" data-testid="analytics-tab-potential">
                            <Target className="mr-1.5 h-4 w-4" /> Potenziale
                        </TabsTrigger>
                        </>
                        )}
                    </TabsList>
                    <TabsContent value="vehicles"><VehicleSales data={vehicles} /></TabsContent>
                    <TabsContent value="workshop"><WorkshopReport data={workshop} /></TabsContent>
                    <TabsContent value="sellers"><SellerRanking data={sellers} /></TabsContent>
                    <TabsContent value="operations"><OperationsReport data={operations} /></TabsContent>
                    <TabsContent value="potential"><PotentialReport data={potential} /></TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
